import { getUniqueLayerName } from 'business/plateviewer/GenerateUtil';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import { RMModal, useModalHandlers } from 'components/modal';
import RMTable, { RowEditProps } from 'components/customComponents/Controls/RMTable';
import React, { useCallback, useEffect, useState } from 'react';
import { IStyle } from 'Styles';
import { getNewId } from 'utils/Global';
import { IngredientsModalType } from './IngredientEnums';
import {
  EMPTY_NAME_ERROR,
  getMaxLengthError,
  NAME_MAX_LENGTH,
} from './IngredientConstants';

const IngredientsModal = (props) => {
  const {
    showModal,
    setShowModal,
    saveModal,
    rowsData,
    dialogClassName,
    heading,
    columns,
    ingredientsModalType,
  } = props;
  const [selectedRows, setSelectedRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [rowEditId, setRowEditId] = useState<number>();
  const [editedRow, setEditedRow] = useState(null);
  const { showLoadingModal, hideModal, showModal: showInfoModal } = useModalHandlers();

  const handleDeleteClick = () => {
    let allRowsCopy = [...allRows];
    allRowsCopy = allRowsCopy.filter(function (value, index) {
      return selectedRows.indexOf(index) == -1;
    });

    setAllRows(allRowsCopy);
    setSelectedRows([]);
  };

  useEffect(() => {
    setAllRows(rowsData);
  }, [rowsData]);

  const handleSelectionChange = (rowIndices) => {
    setSelectedRows([...rowIndices]);
  };

  const handleAddClick = () => {
    const name = getUniqueLayerName(allRows, 'New Type');
    const id = getNewId();
    const newRow = {
      id: id,
      name: name,
    };
    setAllRows([...allRows, newRow]);
  };

  const handleSaveClick = useCallback(async () => {
    const hasEmptyName = allRows?.some((r) => !r?.name || r?.name?.trim().length === 0);
    const hasTooLongName = allRows?.some((r) => (r?.name?.length || 0) > NAME_MAX_LENGTH);

    let errorMessage = '';

    if (hasEmptyName) {
      errorMessage = EMPTY_NAME_ERROR;
    } else if (hasTooLongName) {
      errorMessage = getMaxLengthError(ingredientsModalType);
    }

    if (hasEmptyName || hasTooLongName) {
      showInfoModal({
        body: errorMessage,
        heading: 'Error',
        okButtonText: 'Ok',
        isOkOnly: true,
        onHide() {
          hideModal();
        },
        onOk() {
          hideModal();
        },
      });
      return;
    }

    showLoadingModal('Saving changes');
    setSelectedRows([]);
    const success = await saveModal(allRows);
    if (success) {
      setShowModal(false);
    }
    hideModal();
  }, [setShowModal, setSelectedRows, allRows, showInfoModal, hideModal]);

  const handleCancelClick = async () => {
    setShowModal(false);
    setSelectedRows([]);
    setRowEditId(null);
    setEditedRow(null);
  };

  const rowSaveHandler = (e) => {
    const allRowsCopy = [...allRows];
    allRowsCopy[rowEditId].name = editedRow.name;
    setAllRows(allRowsCopy);

    setRowEditId(null);
    setEditedRow(null);
  };

  const rowDiscardHandler = (e) => {
    setRowEditId(null);
    setEditedRow(null);
  };

  const handleOnCellContentEditComplete = (e) => {
    setEditedRow(e);
  };

  const rowEditProps: RowEditProps = {
    currentEditingRowId: rowEditId,
    setCurrentEditingRowId: setRowEditId,
    handleOnCellContentEditComplete: handleOnCellContentEditComplete,
    showActionButtons: true,
    showEditButton: true,
    rowSaveHandler: rowSaveHandler,
    rowDiscardHandler: rowDiscardHandler,
  };

  return (
    <RMModal
      id={`ingredientsmodal-${heading}-wrapper`}
      showModal={showModal}
      setShowModal={setShowModal}
      onOk={handleSaveClick}
      body={
        <div id='ingredientsmodal-body-wrapper' className="grid grid-flow-row auto-rows-max border-dashed border gap-2">
          <div id="ingredientsmodal-table-container" className="w-full h-96 overflow-y-auto">
            <RMTable
              data={allRows}
              columns={[...columns]}
              selectedIds={selectedRows}
              onCheckboxSelectionChange={handleSelectionChange}
              mainContainerStyle={styles.tableStyle}
              rowEditProps={rowEditProps}
            />
          </div>
          {ingredientsModalType == IngredientsModalType.ProteinType && (
            <div id="ingredientsmodal-warning-wrapper" className="w-[100%] flex flex-row justify-center items-center">
              <RMSvgIcon
                id="ingredientsmodal-svg-warning-icon"
                width={'0.923rem'}
                height={'0.923rem'}
                name="Warning"
                fill="#F2C64B"
              />
              <div id="ingredientsmodal-warning-text" className="text-[#737373] text-[0.923rem] ml-[0.3rem]">
                Changes here will apply to all linked proteins
              </div>
            </div>
          )}
          <div id="ingredientsmodal-actionbar" className="w-full m-2 flex">
            <RMSvgIcon
              id="ingredientsmodal-add-icon"
              className="cursor-pointer w-10 h-10 "
              name="New"
              onClick={handleAddClick}
            />
            <RMCustomButton
              key={`rowRemoveButton`}
              contentContainerClass="cursor-pointer"
              Icon={
                <RMSvgIcon
                  subfolder="Setup"
                  className="cursor-pointer w-10 h-10 ml-1"
                  name="Remove"
                />
              }
              isDisabled={selectedRows.length ? false : true}
              onClick={handleDeleteClick}
            />
          </div>
        </div>
      }
      heading={heading}
      onCancel={handleCancelClick}
      okButtonText="Save"
      dialogClassName={dialogClassName}
    />
  );
};

const styles: IStyle = {
  tableStyle: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },
};

export default IngredientsModal;
